import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { StorageService } from 'src/app/modules/shared/services/storage.service';
import { BlockUIService } from 'src/app/modules/shared/services/blockUI.service';
import { resolveCompanyId } from 'src/app/utils/tenant-context.util';
import { SignalRService } from 'src/app/utils/SignalR.service';
import {
  AuthType,
  EppIntegrationConfig,
  EppIntegrationLogsRequest,
  EppPlaceholder,
  EppSyncLogDTO,
  HttpMethodType,
  SyncStatus,
} from '../../models/epp-integration.models';
import { EppIntegrationService } from '../../services/epp-integration.service';
import { formatInventorySyncJson } from '../../../epps/utils/inventory-sync-json.util';

@Component({
  selector: 'app-epp-integrations',
  templateUrl: './epp-integrations.component.html',
  styleUrl: './epp-integrations.component.scss',
  providers: [ConfirmationService],
})
export class EppIntegrationsComponent implements OnInit, OnDestroy {
  private integrationService = inject(EppIntegrationService);
  private blockUI = inject(BlockUIService);
  private storage = inject(StorageService);
  private signalRService = inject(SignalRService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private fb = inject(FormBuilder);

  // --- Estado lista ---
  integrations: EppIntegrationConfig[] = [];
  listLoading = false;
  companyId: number | null = null;

  // --- Estado formulario ---
  showForm = false;
  mode: 'add' | 'edit' = 'add';
  editingId = 0;
  hasAuthConfigured = false;
  integrationForm!: FormGroup;

  // --- Estado historial ---
  logs: EppSyncLogDTO[] = [];
  logsLoading = false;
  totalLogs = 0;
  logPage = 1;
  logPageSize = 20;
  logFirst = 0;
  filterForm!: FormGroup;
  selectedLog: EppSyncLogDTO | null = null;
  showPayloadDialog = false;
  showResponseDialog = false;

  // --- SignalR ---
  private signalRSubs: Subscription[] = [];

  // --- Opciones estáticas ---
  httpMethods: { label: string; value: HttpMethodType }[] = [
    { label: 'POST', value: 'POST' },
    { label: 'GET', value: 'GET' },
    { label: 'PUT', value: 'PUT' },
    { label: 'PATCH', value: 'PATCH' },
  ];

  authTypes: { label: string; value: AuthType }[] = [
    { label: 'Ninguna', value: 'NONE' },
    { label: 'Basic Auth', value: 'BASIC' },
    { label: 'Bearer Token', value: 'BEARER' },
    { label: 'API Key', value: 'API_KEY' },
  ];

  syncStatusOptions: { label: string; value: SyncStatus | null }[] = [
    { label: 'Todos', value: null },
    { label: 'Pendiente', value: 'PENDING' },
    { label: 'Procesando', value: 'PROCESSING' },
    { label: 'Sincronizado', value: 'SYNCED' },
    { label: 'Error', value: 'ERROR' },
  ];

  availablePlaceholders: EppPlaceholder[] = [];
  placeholdersLoading = false;
  readonly templatePlaceholder = '{\n  "documento": "{{employee_document}}",\n  "fecha": "{{delivery_date_ddmmyyyy}}",\n  "items": [[ { "codigo": "{{item.item_code}}", "cantidad": {{item.quantity}} } ]]\n}';
  configOptions: { label: string; value: number | null }[] = [{ label: 'Todas', value: null }];

  ngOnInit(): void {
    const currentCompany = this.storage.getItem('currentCompany', false) as any;
    this.companyId = resolveCompanyId(currentCompany);

    this.buildIntegrationForm();
    this.buildFilterForm();
    this.loadPlaceholders();

    if (this.companyId) {
      this.loadIntegrations();
    }

    this.subscribeSignalR();
  }

  ngOnDestroy(): void {
    this.signalRSubs.forEach((s) => s.unsubscribe());
  }

  // ============================================================
  // LISTA
  // ============================================================
  loadIntegrations(): void {
    if (!this.companyId) return;
    this.listLoading = true;
    this.integrationService.getByCompany(this.companyId).subscribe({
      next: (res) => {
        this.integrations = res.data ?? [];
        this.configOptions = [
          { label: 'Todas', value: null },
          ...this.integrations.map((c) => ({ label: c.name, value: c.id })),
        ];
        this.listLoading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar las integraciones',
        });
        this.listLoading = false;
      },
    });
  }

  openNew(): void {
    this.mode = 'add';
    this.editingId = 0;
    this.hasAuthConfigured = false;
    this.resetForm();
    this.showForm = true;
  }

  openEdit(config: EppIntegrationConfig): void {
    this.mode = 'edit';
    this.editingId = config.id;
    this.hasAuthConfigured = config.hasAuthConfigured;
    this.patchForm(config);
    this.showForm = true;
  }

  closeForm(): void {
    this.showForm = false;
    this.resetForm();
  }

  confirmDelete(event: Event, id: number): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: '¿Desea eliminar esta integración?',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger p-button-sm',
      accept: () => {
        this.blockUI.start();
        this.integrationService.delete(id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Eliminado',
              detail: 'Integración eliminada correctamente',
            });
            this.loadIntegrations();
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo eliminar la integración',
            });
            this.blockUI.stop();
          },
          complete: () => this.blockUI.stop(),
        });
      },
    });
  }

  save(): void {
    if (this.integrationForm.invalid) return;

    const raw = this.integrationForm.getRawValue();
    const authPayload = this.buildAuthPayload(raw);

    const command = {
      id: this.editingId,
      companyId: this.companyId!,
      name: raw.name,
      description: raw.description,
      isActive: raw.isActive,
      isAutoSync: raw.isAutoSync,
      endpointUrl: raw.endpointUrl,
      httpMethod: raw.httpMethod,
      timeoutSeconds: raw.timeoutSeconds,
      maxRetries: raw.maxRetries,
      authType: raw.authType,
      authPayloadRaw: authPayload,
      payloadTemplate: raw.payloadTemplate ?? null,
    };

    this.blockUI.start();
    this.integrationService.save(command).subscribe({
      next: () => {
        const detail =
          this.mode === 'add' ? 'Integración creada correctamente' : 'Integración actualizada correctamente';
        this.messageService.add({ severity: 'success', summary: 'Guardado', detail });
        this.closeForm();
        this.loadIntegrations();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo guardar la integración',
        });
        this.blockUI.stop();
      },
      complete: () => this.blockUI.stop(),
    });
  }

  // ============================================================
  // HISTORIAL
  // ============================================================
  loadLogs(page = 1): void {
    if (!this.companyId) return;
    const f = this.filterForm.getRawValue();
    this.logPage = page;
    this.logsLoading = true;

    const req: EppIntegrationLogsRequest = {
      companyId: this.companyId,
      pageNumber: page,
      pageSize: this.logPageSize,
      syncStatus: f.syncStatus ?? null,
      dateFrom: f.dateFrom ? (f.dateFrom as Date).toISOString() : null,
      dateTo: f.dateTo ? (f.dateTo as Date).toISOString() : null,
      eppRequestId: f.eppRequestId ?? null,
      configId: f.configId ?? null,
    };

    this.integrationService.getLogs(req).subscribe({
      next: (res) => {
        const data = res.data;
        this.logs = data?.items ?? [];
        this.totalLogs = data?.totalCount ?? 0;
        this.logsLoading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo cargar el historial',
        });
        this.logsLoading = false;
      },
    });
  }

  onLogPageChange(event: any): void {
    this.logPageSize = event.rows;
    this.logFirst = event.first;
    this.loadLogs(Math.floor(event.first / event.rows) + 1);
  }

  clearLogFilters(): void {
    this.filterForm.reset({ syncStatus: null, configId: null });
    this.loadLogs(1);
  }

  retrySync(event: Event, syncLogId: number): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: '¿Desea reintentar esta sincronización?',
      icon: 'pi pi-refresh',
      acceptButtonStyleClass: 'p-button-warning p-button-sm',
      accept: () => {
        this.blockUI.start();
        this.integrationService.retry(syncLogId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'info',
              summary: 'Reintento',
              detail: 'Sincronización reenviada correctamente',
            });
            this.loadLogs(this.logPage);
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo reintentar la sincronización',
            });
            this.blockUI.stop();
          },
          complete: () => this.blockUI.stop(),
        });
      },
    });
  }

  viewPayload(log: EppSyncLogDTO): void {
    this.selectedLog = log;
    this.showPayloadDialog = true;
  }

  viewResponse(log: EppSyncLogDTO): void {
    this.selectedLog = log;
    this.showResponseDialog = true;
  }

  prettyJson(raw: string | null | undefined): string {
    return formatInventorySyncJson(raw);
  }

  // ============================================================
  // FORM HELPERS
  // ============================================================
  get authType(): AuthType {
    return this.integrationForm.get('authType')?.value as AuthType;
  }

  loadPlaceholders(): void {
    this.placeholdersLoading = true;
    this.integrationService.getAvailablePlaceholders().subscribe({
      next: (data) => {
        this.availablePlaceholders = data ?? [];
        this.placeholdersLoading = false;
      },
      error: () => {
        this.placeholdersLoading = false;
      },
    });
  }

  copyPlaceholder(key: string): void {
    navigator.clipboard.writeText(key).then(() => {
      this.messageService.add({
        severity: 'info',
        summary: 'Copiado',
        detail: `${key} copiado al portapapeles`,
        life: 2000,
      });
    });
  }

  private buildIntegrationForm(): void {
    this.integrationForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      isActive: [true],
      isAutoSync: [true],
      endpointUrl: ['', [Validators.required, Validators.pattern('https?://.+')]],
      httpMethod: ['POST', Validators.required],
      timeoutSeconds: [30, [Validators.required, Validators.min(1)]],
      maxRetries: [3, [Validators.required, Validators.min(0)]],
      authType: ['NONE', Validators.required],
      username: [''],
      password: [''],
      token: [''],
      headerName: [''],
      headerValue: [''],
      payloadTemplate: [''],
    });
  }

  private buildFilterForm(): void {
    this.filterForm = this.fb.group({
      syncStatus: [null],
      dateFrom: [null],
      dateTo: [null],
      eppRequestId: [null],
      configId: [null],
    });
  }

  private resetForm(): void {
    this.integrationForm.reset({
      name: '',
      description: '',
      isActive: true,
      isAutoSync: true,
      endpointUrl: '',
      httpMethod: 'POST',
      timeoutSeconds: 30,
      maxRetries: 3,
      authType: 'NONE',
      username: '',
      password: '',
      token: '',
      headerName: '',
      headerValue: '',
      payloadTemplate: '',
    });
  }

  private patchForm(config: EppIntegrationConfig): void {
    this.integrationForm.patchValue({
      name: config.name,
      description: config.description ?? '',
      isActive: config.isActive,
      isAutoSync: config.isAutoSync,
      endpointUrl: config.endpointUrl,
      httpMethod: config.httpMethod,
      timeoutSeconds: config.timeoutSeconds,
      maxRetries: config.maxRetries,
      authType: config.authType,
      payloadTemplate: config.payloadTemplate ?? '',
    });
  }

  private buildAuthPayload(raw: any): string | null {
    switch (raw.authType as AuthType) {
      case 'BASIC':
        if (!raw.username && !raw.password) return null;
        return JSON.stringify({ username: raw.username, password: raw.password });
      case 'BEARER':
        if (!raw.token) return null;
        return JSON.stringify({ token: raw.token });
      case 'API_KEY':
        if (!raw.headerName && !raw.headerValue) return null;
        return JSON.stringify({ headerName: raw.headerName, headerValue: raw.headerValue });
      default:
        return null;
    }
  }

  // ============================================================
  // UI HELPERS
  // ============================================================
  getAuthTypeLabel(authType: AuthType): string {
    const map: Record<AuthType, string> = {
      NONE: 'Sin auth',
      BASIC: 'Basic Auth',
      BEARER: 'Bearer Token',
      API_KEY: 'API Key',
    };
    return map[authType] ?? authType;
  }

  getSyncStatusSeverity(status: SyncStatus): 'success' | 'secondary' | 'info' | 'warning' | 'danger' | 'contrast' | undefined {
    const map: Record<SyncStatus, 'success' | 'secondary' | 'info' | 'warning' | 'danger'> = {
      PENDING: 'warning',
      PROCESSING: 'info',
      SYNCED: 'success',
      ERROR: 'danger',
    };
    return map[status] ?? 'secondary';
  }

  getSyncStatusLabel(status: SyncStatus): string {
    const map: Record<SyncStatus, string> = {
      PENDING: 'Pendiente',
      PROCESSING: 'Procesando',
      SYNCED: 'Sincronizado',
      ERROR: 'Error',
    };
    return map[status] ?? status;
  }

  // ============================================================
  // SIGNALR
  // ============================================================
  private subscribeSignalR(): void {
    const syncSub = this.signalRService.eppDeliverySyncEvent$.subscribe((data) => {
      if (data.status === 'SYNCED') {
        this.messageService.add({
          severity: 'success',
          summary: 'Integración sincronizada',
          detail: `Entrega #${data.eppRequestId} sincronizada correctamente`,
        });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Integración fallida',
          detail: `Error en entrega #${data.eppRequestId} (HTTP ${data.httpStatus}). Revise el historial.`,
          life: 8000,
        });
      }
    });

    const failedSub = this.signalRService.eppDeliverySyncFailedEvent$.subscribe((data) => {
      this.messageService.add({
        severity: 'error',
        summary: 'Error de integración',
        detail: data.error,
        life: 8000,
      });
    });

    this.signalRSubs.push(syncSub, failedSub);
  }
}
